import { reactive } from "vue";

// Глобальный аудиоплеер (модульный синглтон): на всё приложение играет один
// трек через один <audio>. Состояние переживает ремонты virtua и смену чатов —
// пузырь в ленте и глобальная полоса сверху читают один и тот же стейт.

export interface AudioTrack {
    // Ключ трека — URL вложения (уникален у файла).
    id: string;
    // objectURL расшифрованного блоба.
    src: string;
    title: string;
    // Длительность из метаданных вложения — фолбэк, пока/если плеер не знает
    // свою (webm от MediaRecorder может отдавать Infinity).
    durationHint?: number;
}

const state = reactive({
    track: null as AudioTrack | null,
    playing: false,
    time: 0,
    duration: 0,
});

let audio: HTMLAudioElement | null = null;

function syncDuration(): void {
    if (!audio) {
        return;
    }
    state.duration =
        Number.isFinite(audio.duration) && audio.duration > 0
            ? audio.duration
            : (state.track?.durationHint ?? 0);
}

function ensureAudio(): HTMLAudioElement {
    if (!audio) {
        audio = new Audio();
        audio.addEventListener("timeupdate", () => {
            state.time = audio!.currentTime;
        });
        audio.addEventListener("play", () => {
            state.playing = true;
        });
        // pause прилетает и на ended.
        audio.addEventListener("pause", () => {
            state.playing = false;
        });
        audio.addEventListener("ended", () => {
            state.time = state.duration;
        });
        audio.addEventListener("loadedmetadata", syncDuration);
        audio.addEventListener("durationchange", syncDuration);
    }
    return audio;
}

export function formatTime(sec: number): string {
    const s = Math.max(0, Math.floor(sec));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export function useAudioPlayer() {
    // Старт трека; повторный вызов для текущего — пауза/продолжить.
    function play(track: AudioTrack): void {
        const a = ensureAudio();
        if (state.track?.id === track.id) {
            toggle();
            return;
        }
        state.track = track;
        state.time = 0;
        state.duration = track.durationHint ?? 0;
        a.src = track.src;
        void a.play();
    }

    function toggle(): void {
        if (!audio || !state.track) {
            return;
        }
        if (audio.paused) {
            void audio.play();
        } else {
            audio.pause();
        }
    }

    // Перемотка на долю длительности; с паузы — продолжает играть.
    function seek(ratio: number): void {
        if (!audio || !state.track || state.duration <= 0) {
            return;
        }
        audio.currentTime = Math.min(1, Math.max(0, ratio)) * state.duration;
        if (audio.paused) {
            void audio.play();
        }
    }

    function close(): void {
        if (audio) {
            audio.pause();
            audio.removeAttribute("src");
        }
        state.track = null;
        state.playing = false;
        state.time = 0;
        state.duration = 0;
    }

    return { state, play, toggle, seek, close };
}
